import { format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TableLoading from '@components/common/TableLoading/TableLoading';
import TableLoadingTrigger from '@components/common/TableLoadingTrigger/TableLoadingTrigger';
import Button from '@components/uikit/Button/Button';
import FilterSearch from '@components/uikit/FilterSearch/FilterSearch';

import { getFullOrShortList } from '@helpers/getFullOrShortList';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useEquipmentSettingsLogListInfiniteQuery } from '../../services/useEquipmentSettingsLogListInfiniteQuery';
import TableMultiplicationFactorsLogSkeleton from './TableMultiplicationFactorsLogSkeleton';
import style from './style.module.scss';

const MultiplicationFactorsLog = () => {
  const { t } = useTranslation();
  const { data: profileData } = useUserProfileQuery();

  const [searchValue, setSearchValue] = useState('');
  const [showTableList, setShowTableList] = useState(false);

  const [debouncedValue] = useDebouncedValue(searchValue, 500);

  const {
    data: multiFactorsData,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isLoading,
    isRefetching,
  } = useEquipmentSettingsLogListInfiniteQuery({ perPage: 20, search: debouncedValue });

  const [ref, isInViewPort] = useIntersectionObserver({
    threshold: 0.1,
    reInitializeOnChange: isFetching,
  });

  useEffect(() => {
    if (isInViewPort && hasNextPage && showTableList && !isFetching) {
      fetchNextPage();
    }
  }, [isInViewPort, hasNextPage, isFetching]);

  const dateTimeFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const multiFactorsTableData = multiFactorsData?.pages?.flatMap((page) => page.data) ?? [];
  const fullOrShortMultiFactorsTableData = getFullOrShortList(multiFactorsTableData, showTableList, 4);
  const isTableLoading = isLoading || isRefetching;

  return (
    <div className={style.multiplicationFactorsLogWrapper}>
      {multiFactorsTableData.length > 4 && (
        <div className={style.hideLogsButtonWrapper}>
          <Button
            text={t(showTableList ? 'Hide logs' : 'Show all logs')}
            type="secondary"
            onClick={() => setShowTableList(!showTableList)}
          />
        </div>
      )}
      <div className={style.multiplicationFactorsLogTableWrapper}>
        <div className={style.multiplicationFactorsLogHeader}>
          <h4 className={style.multiplicationFactorsLogTitle}>{t('MULTIPLICATION FACTOR CHANGE LOG')}</h4>
          <div className={style.searchPanel}>
            <FilterSearch
              value={searchValue}
              onChange={setSearchValue}
              placeholder={t('Start to type...')}
              variant="table-search"
            />
          </div>
        </div>
        <table className={style.multiplicationFactorsLogTable}>
          <thead>
            <tr>
              <th>{t('Event')}</th>
              <th>{t('Info')}</th>
              <th>{t('Username')}</th>
              <th>{t('Created At')}</th>
            </tr>
          </thead>
          <tbody>
            {isTableLoading ? (
              <TableMultiplicationFactorsLogSkeleton />
            ) : fullOrShortMultiFactorsTableData?.length ? (
              fullOrShortMultiFactorsTableData.map((row) => (
                <tr key={uuidv4()} className={style.multiplicationFactorsLogBodyRow}>
                  <td>{t(row.action)}</td>
                  <td>
                    <div className={style.messageCell}>{row.notes}</div>
                  </td>
                  <td>{row.user_name}</td>
                  <td className={style.uppercaseCell}>
                    {format(fromZonedTime(row.created_at, userTimezone), dateTimeFormat)}
                  </td>
                </tr>
              ))
            ) : (
              <TableEmptyRowExtended text={t('No multiplication factors log were found')} />
            )}
            <TableLoadingTrigger show={!isFetching} ref={ref} />
            <TableLoading show={isFetching && showTableList} />
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MultiplicationFactorsLog;
